import QRCode from 'qrcode';
import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { Notice } from '@/components/admin/Notice';
import * as authApi from '@/features/auth/api';
import { ApiError } from '@/lib/api/http';

/**
 * 2要素認証（TOTP）の設定・検証画面（指示書 §6-2 / §10）。
 * 未設定なら秘密鍵とバックアップコードを発行し、設定済みならコード入力のみ。
 * QRコードは `provisioning_uri` から画面内で生成する（外部サービスへ送信しない）。
 */
export function TotpPage(): ReactElement {
  const { user, setUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const [setup, setSetup] = useState<authApi.TotpSetup | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const enrolled = user?.totp_enrolled ?? false;

  useEffect(() => {
    if (user && !user.totp_required) navigate('/');
    if (user && user.totp_verified) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    if (!setup) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    void QRCode.toDataURL(setup.provisioning_uri, { margin: 1, width: 220 }).then((url) => {
      if (!cancelled) setQrDataUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [setup]);

  async function startSetup(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setSetup(await authApi.setupTotp());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '設定を開始できませんでした。');
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const updated = enrolled ? await authApi.verifyTotp(code) : await authApi.confirmTotp(code);
      setUser(updated);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '認証に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function handleSignOut(): Promise<void> {
    setSigningOut(true);
    try {
      await signOut();
      navigate('/login');
    } finally {
      setSigningOut(false);
    }
  }

  async function onSubmitBackupCode(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const updated = await authApi.verifyBackupCode(backupCode);
      setUser(updated);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'バックアップコードが違います。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-wpcanvas px-4">
      <div className="w-full max-w-md rounded-sm border border-wpborder bg-white px-5 py-6 shadow-sm">
        <h1 className="mb-4 text-lg font-semibold text-wptext-strong">2要素認証</h1>

        {error && (
          <Notice kind="error" dismissible={false}>
            {error}
          </Notice>
        )}

        {!enrolled && !setup && (
          <>
            <p className="mb-4 text-wptext">
              事務局・スーパー管理者のアカウントには2要素認証の設定が必要です。
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={() => void startSetup()}
              className="rounded-sm bg-wpaccent px-3 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
            >
              設定を開始する
            </button>
          </>
        )}

        {setup && (
          <div className="mb-5">
            <p className="mb-2 text-wptext">
              認証アプリ（Google Authenticator、Microsoft Authenticator など）でQRコードを
              読み取ってください。表示されるのはこの1回だけです。
            </p>
            <div className="mb-3 flex justify-center rounded-sm border border-wpborder bg-wpcanvas p-3">
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="TOTP設定用QRコード" width={220} height={220} />
              ) : (
                <div className="flex h-[220px] w-[220px] items-center justify-center text-wptext-muted">
                  生成中…
                </div>
              )}
            </div>
            <details className="mb-3">
              <summary className="cursor-pointer text-wptext-muted">
                QRコードを読み取れない場合（キーを手入力）
              </summary>
              <code className="mt-2 block break-all rounded-sm bg-wpcanvas px-2 py-1.5 font-mono">
                {setup.secret}
              </code>
            </details>
            <p className="mb-1 font-semibold text-wptext-strong">バックアップコード</p>
            <ul className="mb-2 grid grid-cols-2 gap-1 font-mono">
              {setup.backup_codes.map((backupCode) => (
                <li key={backupCode}>{backupCode}</li>
              ))}
            </ul>
            <p className="text-xs text-wptext-muted">
              端末を紛失した場合に使います。安全な場所に保管してください。
            </p>
          </div>
        )}

        {(enrolled || setup) && !useBackupCode && (
          <form onSubmit={(event) => void onSubmit(event)}>
            <label htmlFor="totp-code" className="mb-1.5 block text-wptext-muted">
              認証アプリに表示されている6桁のコード
            </label>
            <input
              id="totp-code"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(event) => setCode(event.target.value)}
              className="mb-4 w-full rounded-sm border border-wpborder px-3 py-2 font-mono tracking-widest"
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-sm bg-wpaccent px-3 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
            >
              {busy ? '確認中…' : '確認する'}
            </button>
          </form>
        )}

        {enrolled && !useBackupCode && (
          <button
            type="button"
            onClick={() => {
              setUseBackupCode(true);
              setError(null);
            }}
            className="mt-3 text-wptext-muted hover:underline"
          >
            認証アプリが使えない場合（バックアップコードを使う）
          </button>
        )}

        {enrolled && useBackupCode && (
          <form onSubmit={(event) => void onSubmitBackupCode(event)}>
            <label htmlFor="backup-code" className="mb-1.5 block text-wptext-muted">
              バックアップコード（設定時に表示された、ハイフン区切りのコード）
            </label>
            <input
              id="backup-code"
              name="backupCode"
              autoComplete="off"
              required
              value={backupCode}
              onChange={(event) => setBackupCode(event.target.value)}
              className="mb-1 w-full rounded-sm border border-wpborder px-3 py-2 font-mono tracking-widest"
            />
            <p className="mb-4 text-xs text-wptext-muted">
              1つのコードは1回しか使えません。使い切った場合は事務局・スーパー管理者に
              TOTPのリセットを依頼してください。
            </p>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-sm bg-wpaccent px-3 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
            >
              {busy ? '確認中…' : '確認する'}
            </button>
            <button
              type="button"
              onClick={() => {
                setUseBackupCode(false);
                setError(null);
              }}
              className="mt-2 text-wptext-muted hover:underline"
            >
              ← 6桁のコード入力に戻る
            </button>
          </form>
        )}

        <button
          type="button"
          disabled={signingOut}
          onClick={() => void handleSignOut()}
          className="mt-4 text-wptext-muted hover:underline disabled:opacity-50"
        >
          {signingOut ? 'ログアウト中…' : '別のアカウントでログインし直す（ログアウト）'}
        </button>
      </div>
    </div>
  );
}
